/**
 * Skill bind keys on krybot agent capabilities (target architecture 2026-07-22).
 * Prefer writing under capabilities; default_context remains readable via resolveAgentSkillBundles.
 */

export const SKILL_BIND_KEYS = [
  'skill_bundle',
  'bridge_krybot_agent_id',
  'include_skill_file_in_prompt',
] as const;

export interface AgentSkillBinding {
  skill_bundle: string;
  bridge_krybot_agent_id: string;
  /** When false, materials loader skips SKILL append. Undefined = treat as include. */
  include_skill_file_in_prompt: boolean;
}

export function readCapabilitiesSkillBinding(
  capabilities?: Record<string, unknown> | null
): AgentSkillBinding {
  const caps = capabilities && typeof capabilities === 'object' ? capabilities : {};
  return {
    skill_bundle: typeof caps.skill_bundle === 'string' ? caps.skill_bundle.trim() : '',
    bridge_krybot_agent_id:
      typeof caps.bridge_krybot_agent_id === 'string' ? caps.bridge_krybot_agent_id.trim() : '',
    include_skill_file_in_prompt: caps.include_skill_file_in_prompt !== false,
  };
}

/** Merge skill bind fields into capabilities (canonical write location). */
export function patchCapabilitiesSkillBinding(
  capabilities: Record<string, unknown> | null | undefined,
  patch: Partial<AgentSkillBinding>
): Record<string, unknown> {
  const next: Record<string, unknown> = {
    ...(capabilities && typeof capabilities === 'object' ? capabilities : {}),
  };

  if (patch.skill_bundle !== undefined) {
    const v = patch.skill_bundle.trim();
    if (v) next.skill_bundle = v;
    else delete next.skill_bundle;
  }

  if (patch.bridge_krybot_agent_id !== undefined) {
    const v = patch.bridge_krybot_agent_id.trim();
    if (v) next.bridge_krybot_agent_id = v;
    else delete next.bridge_krybot_agent_id;
  }

  if (patch.include_skill_file_in_prompt !== undefined) {
    if (patch.include_skill_file_in_prompt) {
      // Prefer omitting when true (default include) to keep blob small
      delete next.include_skill_file_in_prompt;
    } else {
      next.include_skill_file_in_prompt = false;
    }
  }

  return next;
}

/** Extract markdown table / list paths under a "## Reference Files" heading. */
export function extractReferencePathsFromSkillMarkdown(content: string): string[] {
  if (!content.trim()) return [];
  const lines = content.split(/\r?\n/);
  let inSection = false;
  const paths: string[] = [];
  const seen = new Set<string>();

  for (const line of lines) {
    if (/^#{1,3}\s+reference\s+files\b/i.test(line.trim())) {
      inSection = true;
      continue;
    }
    if (inSection && /^#{1,3}\s+/.test(line.trim())) {
      break;
    }
    if (!inSection) continue;

    // Table row: | path | ... |
    const tableCell = line.match(/^\|\s*`?([^\s|`]+\.md)`?\s*\|/i);
    if (tableCell?.[1]) {
      const p = tableCell[1].trim();
      if (!seen.has(p)) {
        seen.add(p);
        paths.push(p);
      }
      continue;
    }
    // Bullet: - references/foo.md
    const bullet = line.match(/^\s*[-*]\s+`?((?:references\/)?[^\s`]+\.md)`?/i);
    if (bullet?.[1]) {
      const p = bullet[1].trim();
      if (!seen.has(p)) {
        seen.add(p);
        paths.push(p);
      }
    }
  }
  return paths;
}
